import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getSelectedAssessmentPeriodId } from '../../../common/selectors/auth';

import DashboardCardLeft from './DashboardCardLeftOneCol';
import { H4 } from 'common/styles/page';

import DoughnutGauge from './DoughnutGauge';

import { AssessmentPeriodSurveyCompletion } from '../../../store/generated/portal/assessments/core';

interface AssessmentProgressLeftProps {
  assessment_progress_data: AssessmentPeriodSurveyCompletion;
}

const DashboardAssessmentProgressLeft: React.FC<AssessmentProgressLeftProps> = ({
  assessment_progress_data,
}) => {
  const [departmentScores, setDepartmentScores] = useState<
    { name: string; total_requests: number; total_responses: number; percent_complete: number }[]
  >([]);
  const [overallScores, setOverallScores] = useState<{
    total_requests: number;
    total_responses: number;
    percent_complete: number;
  }>();
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);

  useEffect(() => {
    if (assessment_progress_data) {
      const departmentResponseData = assessment_progress_data?.department_responses || [];
      const scores = departmentResponseData
        .map(
          (item: {
            department_name: string;
            total_requests: number;
            total_responses: number;
            percent_complete: number;
          }) => ({
            name: item.department_name,
            total_requests: item.total_requests,
            total_responses: item.total_responses,
            percent_complete: parseFloat((item.percent_complete * 100).toFixed(1)),
          })
        )
        .sort((a: { name: any }, b: { name: string }) => a.name.localeCompare(b.name));

      setDepartmentScores(scores);
      setOverallScores({
        total_requests: assessment_progress_data.total_requests,
        total_responses: assessment_progress_data.total_responses,
        percent_complete: assessment_progress_data.percent_complete * 100 || 0,
      });
    }
  }, [assessment_progress_data, selectedAssessmentPeriodId]);

  if (Array.isArray(departmentScores) && overallScores) {
    return (
      <>
        <DashboardCardLeft
          title="Overall Progress"
          content={
            <Container>
              <Row>
                <DoughnutGauge
                  complete={overallScores?.percent_complete}
                  percent={true}
                  size="tiny"
                />
                <Content>
                  <H4>All Teams</H4>
                  <Caption>{`${overallScores?.total_requests} requests, ${overallScores?.total_responses} responses`}</Caption>
                </Content>
              </Row>
            </Container>
          }
        />
        <DashboardCardLeft
          title="Team Progress"
          content={
            <Container>
              {departmentScores.map((dept, index) => (
                <Row key={index}>
                  <DoughnutGauge complete={dept.percent_complete} percent={true} size="tiny" />
                  <Content>
                    <H4>{dept.name}</H4>
                    <Caption>{`${dept.total_requests} requests, ${dept.total_responses} responses`}</Caption>
                  </Content>
                </Row>
              ))}
            </Container>
          }
        />
      </>
    );
  }
};

export default DashboardAssessmentProgressLeft;

// Styled components

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 220px; // constrain the name + caption area
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const Caption = styled.div`
  font-family: 'Poppins';
  font-size: 12px;
  line-height: 16px;
  color: #777;
  white-space: normal;
  word-wrap: break-word;
`;
